// Schemas de entrada (zod). Usados pela API para validar, pela web nos formulários
// e pelo servidor MCP para descrever as ferramentas.
import { z } from 'zod';
import {
  AGENT_SCOPES,
  COMMENT_KINDS,
  CRITERION_STATUSES,
  EXECUTION_STATUSES,
  LINK_TYPES,
  PRIORITIES,
  PROJECT_KEY_RE,
  PROJECT_STATUSES,
  RELEASE_PLATFORMS,
  RELEASE_STATUSES,
  RELEASE_TYPES,
  REF_KINDS,
  TASK_ARCHIVED_FILTERS,
  TASK_DATE_FIELDS,
  TASK_KEY_RE,
  TASK_STATUSES,
  TASK_TYPES,
  USER_ROLES,
  VALIDATION_KINDS,
  VALIDATION_STATUSES,
} from './domain.ts';

const text = (max: number) => z.string().trim().max(max);
const required = (max: number) => z.string().trim().min(1, 'obrigatório').max(max);

export const zTaskKey = z
  .string()
  .trim()
  .transform((s) => s.toUpperCase())
  .pipe(z.string().regex(TASK_KEY_RE, 'formato esperado: JYN-123'));

export const zProjectKey = z
  .string()
  .trim()
  .toLowerCase()
  .regex(PROJECT_KEY_RE, 'use letras minúsculas, números e hífen (2 a 40 caracteres)');

export const zUrl = z.url({ protocol: /^https?$/ }).max(2000);

/** Data (YYYY-MM-DD) ou data/hora ISO. */
export const zDate = z
  .string()
  .trim()
  .refine((s) => /^\d{4}-\d{2}-\d{2}/.test(s) && !Number.isNaN(Date.parse(s)), 'data inválida (use YYYY-MM-DD)');

export const zStatus = z.enum(TASK_STATUSES);
export const zType = z.enum(TASK_TYPES);
export const zPriority = z.enum(PRIORITIES);

// ---------- Projetos ----------

export const zRepository = z.object({
  name: text(100).optional(),
  url: required(500),
  defaultBranch: text(100).optional(),
  path: text(200).optional().describe('Subpasta do monorepo, se o projeto não ocupa o repositório todo'),
});

export const zModule = z.object({
  key: z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9-_/.]{0,59}$/, 'chave do módulo inválida'),
  name: required(100),
  description: text(1000).optional(),
});

export const zEnvironment = z.object({
  name: required(60),
  url: zUrl.optional(),
  notes: text(500).optional(),
});

export const zProjectLink = z.object({
  title: required(150),
  url: zUrl,
  kind: z.enum(REF_KINDS).default('doc'),
});

export const zAgentConfig = z.object({
  enabled: z.boolean().default(true),
  instructions: text(5000).optional().describe('Regras específicas do projeto para agentes'),
  requireAcceptanceCriteria: z.boolean().default(true),
  defaultLabels: z.array(z.string().trim().max(50)).max(10).default([]),
  requireHumanReview: z.boolean().default(false).describe('Trabalho de agente sempre passa por Em Revisão e só uma pessoa conclui'),
});

export const zProjectCreate = z.object({
  key: zProjectKey,
  name: required(120),
  description: text(5000).optional(),
  status: z.enum(PROJECT_STATUSES).default('active'),
  priority: zPriority.default('medium'),
  repositories: z.array(zRepository).max(20).default([]),
  technologies: z.array(required(60)).max(40).default([]),
  modules: z.array(zModule).max(100).default([]),
  environments: z.array(zEnvironment).max(20).default([]),
  ownerIds: z.array(z.string()).max(20).default([]),
  links: z.array(zProjectLink).max(50).default([]),
  agentConfig: zAgentConfig.default({ enabled: true, requireAcceptanceCriteria: true, defaultLabels: [], requireHumanReview: false }),
});
export type ProjectCreateInput = z.input<typeof zProjectCreate>;

export const zProjectUpdate = zProjectCreate.omit({ key: true }).partial().extend({
  // Sem default nos parciais: só altera o que veio.
  status: z.enum(PROJECT_STATUSES).optional(),
  priority: zPriority.optional(),
  agentConfig: zAgentConfig.partial().optional(),
});
export type ProjectUpdateInput = z.input<typeof zProjectUpdate>;

// ---------- Tarefas ----------

export const zActorRef = z.object({
  kind: z.enum(['user', 'agent']),
  id: required(64),
});

export const zRef = z.object({
  kind: z.enum(REF_KINDS),
  url: required(2000).describe('URL ou caminho (para arquivos: caminho relativo ao repositório)'),
  title: text(200).optional(),
});

export const zTaskCreate = z.object({
  title: required(200),
  description: text(50_000).optional().describe('Markdown'),
  type: zType.optional().describe('Padrão: do modelo ou "task"'),
  priority: zPriority.optional().describe('Padrão: do modelo ou "medium"'),
  status: z.enum(['backlog', 'todo', 'in_progress']).default('backlog'),
  projectKey: zProjectKey.describe('Projeto principal'),
  templateId: z.string().regex(/^[a-f0-9]{24}$/).optional().describe('Modelo de tarefa: preenche tipo, prioridade, descrição, critérios, checklist, etiquetas e estimativa (o que for informado prevalece; listas são somadas)'),
  relatedProjectKeys: z.array(zProjectKey).max(5).default([]).describe('Outros projetos afetados, só quando necessário'),
  module: text(60).optional(),
  labels: z.array(z.string().trim().min(1).max(50)).max(15).default([]),
  assignee: z.union([zActorRef, z.literal('me')]).optional(),
  source: text(500).optional().describe('Origem da demanda (pedido do usuário, issue, incidente...)'),
  acceptanceCriteria: z.array(required(1000)).max(30).default([]),
  checklist: z.array(required(500)).max(50).default([]),
  parentKey: zTaskKey.optional(),
  storyKey: zTaskKey.optional().describe('História à qual a tarefa pertence (tarefa do tipo story)'),
  epicKey: zTaskKey.optional().describe('Só para histórias: épico ao qual a história pertence'),
  releaseId: z.string().regex(/^[a-f0-9]{24}$/).optional().describe('Versão (release) planejada em que a tarefa entra'),
  sprintId: z.string().regex(/^[a-f0-9]{24}$/).optional().describe('Sprint (planejada ou ativa) do mesmo projeto'),
  startDate: zDate.optional().describe('Início planejado (roadmap)'),
  estimateMinutes: z.number().int().min(0).max(100_000).optional().describe('Estimativa em minutos'),
  dueDate: zDate.optional(),
  refs: z.array(zRef).max(30).default([]),
  confirmNew: z.boolean().default(false).describe('true depois de conferir as possíveis duplicatas retornadas'),
});
export type TaskCreateInput = z.input<typeof zTaskCreate>;

export const zCriterionUpdate = z.object({
  id: required(40),
  status: z.enum(CRITERION_STATUSES).optional(),
  note: text(2000).optional().describe('Evidência (met) ou justificativa (waived/not_met)'),
  text: text(1000).optional(),
});

export const zChecklistUpdate = z.object({
  id: required(40),
  done: z.boolean().optional(),
  text: text(500).optional(),
});

export const zTaskUpdate = z.object({
  title: required(200).optional(),
  description: text(50_000).optional(),
  type: zType.optional(),
  priority: zPriority.optional(),
  module: text(60).nullable().optional(),
  relatedProjectKeys: z.array(zProjectKey).max(5).optional(),
  assignee: z.union([zActorRef, z.literal('me')]).nullable().optional(),
  source: text(500).optional(),
  dueDate: zDate.nullable().optional(),
  parentKey: zTaskKey.nullable().optional(),
  storyKey: zTaskKey.nullable().optional().describe('Vincular a uma história (null desvincula)'),
  agentReady: z.boolean().optional().describe('Somente usuários: coloca/retira a tarefa da fila de agentes'),
  epicKey: zTaskKey.nullable().optional().describe('Só para histórias: vincular a um épico (null desvincula)'),
  releaseId: z.string().regex(/^[a-f0-9]{24}$/).nullable().optional().describe('Versão planejada (null desvincula)'),
  sprintId: z.string().regex(/^[a-f0-9]{24}$/).nullable().optional().describe('Sprint (null tira da sprint)'),
  startDate: zDate.nullable().optional(),
  estimateMinutes: z.number().int().min(0).max(100_000).nullable().optional(),
  implementationSummary: text(50_000).optional().describe('O que foi realmente implementado (Markdown)'),
  testSummary: text(20_000).optional().describe('Como foi validado e o resultado (Markdown)'),
  pendingNotes: text(20_000).optional().describe('Pendências conhecidas'),
  acceptanceCriteria: z
    .object({
      add: z.array(required(1000)).max(30).default([]),
      update: z.array(zCriterionUpdate).max(30).default([]),
      remove: z.array(z.string()).max(30).default([]),
    })
    .optional(),
  checklist: z
    .object({
      add: z.array(required(500)).max(50).default([]),
      update: z.array(zChecklistUpdate).max(50).default([]),
      remove: z.array(z.string()).max(50).default([]),
    })
    .optional(),
  refs: z
    .object({
      add: z.array(zRef).max(30).default([]),
      remove: z.array(z.string()).max(30).default([]),
    })
    .optional(),
  expectedVersion: z.number().int().optional().describe('Controle de concorrência otimista'),
});
export type TaskUpdateInput = z.input<typeof zTaskUpdate>;

export const zTransition = z.object({
  to: zStatus,
  reason: text(2000).optional(),
  limitations: text(5000).optional().describe('Ao concluir sem validações: o que não pôde ser validado e por quê'),
  overrideReason: text(2000).optional().describe('Somente admin: força a conclusão ignorando as regras'),
  expectedVersion: z.number().int().optional(),
});
export type TransitionInput = z.input<typeof zTransition>;

export const zMove = z.object({
  to: zStatus,
  beforeKey: zTaskKey.optional().describe('Card que fica logo acima'),
  afterKey: zTaskKey.optional().describe('Card que fica logo abaixo'),
  reason: text(2000).optional(),
  limitations: text(5000).optional(),
});

export const zComment = z.object({
  body: required(20_000),
  kind: z.enum(COMMENT_KINDS).default('comment'),
  executionId: z.string().optional(),
  parentId: z.string().regex(/^[a-f0-9]{24}$/).optional().describe('Responde a este comentário (a conversa fica no comentário raiz)'),
});

export const zLabelCreate = z.object({
  name: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9-]{0,39}$/, 'use minúsculas, números e hífen'),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default('#64748b'),
  description: text(300).optional(),
  projectKey: zProjectKey.nullable().optional().describe('Vazio = etiqueta global'),
});

export const zLabelUpdate = zLabelCreate.omit({ name: true, projectKey: true }).partial().extend({
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
});

export const zTaskLabels = z.object({
  add: z.array(z.string().trim().min(1).max(50)).max(15).default([]),
  remove: z.array(z.string().trim().min(1).max(50)).max(15).default([]),
  createMissing: z.boolean().default(false).describe('Cria etiquetas inexistentes (desde que não sejam parecidas com uma existente)'),
});

export const zLinkTasks = z.object({
  type: z.enum(LINK_TYPES),
  target: zTaskKey,
});

export const zBlocker = z.object({
  reason: required(5000),
  blockedByKey: zTaskKey.optional().describe('Tarefa que causa o bloqueio, se houver'),
  executionId: z.string().optional(),
});

export const zResolveBlocker = z.object({
  resolution: required(5000),
  resumeStatus: z.enum(['backlog', 'todo', 'in_progress', 'in_review']).optional(),
});

export const zValidation = z.object({
  kind: z.enum(VALIDATION_KINDS),
  status: z.enum(VALIDATION_STATUSES),
  command: text(1000).optional().describe('Comando executado, ex.: npm test'),
  summary: required(5000).describe('Resultado real: quantos testes, o que falhou'),
  output: text(20_000).optional().describe('Trecho relevante da saída (será truncado)'),
  executionId: z.string().optional(),
  criterionIds: z.array(z.string()).max(30).default([]),
});

export const zActivityLog = z.object({
  message: required(5000),
  kind: z.enum(['note', 'progress', 'discovery', 'decision']).default('note'),
  taskKey: zTaskKey.optional(),
  projectKey: zProjectKey.optional(),
  executionId: z.string().optional(),
});

export const zExecutionStart = z.object({
  taskKey: zTaskKey,
  sessionId: text(200).optional().describe('ID da sessão do Claude Code (o hook SessionStart informa)'),
  idempotencyKey: text(200).optional(),
  repository: text(500).optional(),
  branch: text(200).optional(),
  agentName: text(100).optional(),
  model: text(100).optional(),
  goal: text(5000).optional().describe('O que esta execução pretende fazer'),
});

export const zExecutionFinish = z.object({
  status: z.enum(EXECUTION_STATUSES.filter((s) => s !== 'running') as ['succeeded', 'partial', 'failed', 'aborted']),
  summary: required(20_000),
  commits: z.array(text(200)).max(50).default([]),
  pullRequests: z.array(text(500)).max(20).default([]),
  filesChanged: z.array(text(500)).max(300).default([]),
  remaining: text(10_000).optional().describe('O que ficou pendente'),
  error: text(10_000).optional().describe('Erro, se falhou'),
});

export const zIntegrationError = z.object({
  operation: required(100),
  message: required(5000),
  taskKey: zTaskKey.optional(),
  projectKey: zProjectKey.optional(),
  occurredAt: zDate.optional(),
  details: z.record(z.string(), z.unknown()).optional(),
});

// ---------- Consultas ----------

const csv = <T extends z.ZodType>(item: T) =>
  z.preprocess((v) => {
    if (v === undefined || v === '') return undefined;
    if (Array.isArray(v)) return v.flatMap((x) => String(x).split(',')).filter(Boolean);
    return String(v).split(',').filter(Boolean);
  }, z.array(item).optional());

const bool = z.preprocess((v) => (v === 'true' || v === true ? true : v === 'false' || v === false ? false : undefined), z.boolean().optional());

export const zTaskQuery = z.object({
  q: z.string().trim().max(500).optional(),
  project: csv(zProjectKey),
  status: csv(zStatus),
  type: csv(zType),
  priority: csv(zPriority),
  labels: csv(z.string().trim().toLowerCase()),
  assignee: z.string().trim().optional().describe('me | none | user:<id> | agent:<id>'),
  module: z.string().trim().optional(),
  blocked: bool,
  agentReady: bool.describe('Só tarefas na fila de agentes'),
  overdue: bool,
  includeClosed: bool,
  parentKey: zTaskKey.optional(),
  story: zTaskKey.optional().describe('Tarefas da história'),
  epic: zTaskKey.optional().describe('Histórias do épico'),
  release: z.string().regex(/^[a-f0-9]{24}$/).optional().describe('Tarefas da versão'),
  sprint: z.string().regex(/^[a-f0-9]{24}$/).optional().describe('Tarefas da sprint'),
  updatedSince: zDate.optional(),
  dueBefore: zDate.optional(),
  archived: z.enum(TASK_ARCHIVED_FILTERS).default('exclude').describe('Cards arquivados (fora do board): exclude (padrão), include ou only'),
  dateField: z.enum(TASK_DATE_FIELDS).optional().describe('Data usada no período from/to: created, updated, closed (concluída ou cancelada), due, archived'),
  from: zDate.optional().describe('Início do período (inclusive)'),
  to: zDate.optional().describe('Fim do período (inclusive)'),
  sort: z.enum(['relevance', 'updated', 'created', 'priority', 'due', 'rank', 'key', 'closed', 'archived']).default('updated'),
  order: z.enum(['asc', 'desc']).default('desc'),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type TaskQuery = z.output<typeof zTaskQuery>;

/** Arquivar/desarquivar cards (só concluídos ou cancelados saem do board). */
export const zTaskArchive = z.object({
  keys: z.array(zTaskKey).min(1).max(500),
});

export const zActivityQuery = z.object({
  project: zProjectKey.optional(),
  taskKey: zTaskKey.optional(),
  actorKind: z.enum(['user', 'agent', 'integration', 'system']).optional(),
  executionId: z.string().optional(),
  before: zDate.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const zDashboardQuery = z.object({
  project: csv(zProjectKey),
  from: zDate.optional(),
  to: zDate.optional(),
  status: csv(zStatus),
  priority: csv(zPriority),
  assignee: z.string().trim().optional(),
});

// ---------- Administração ----------

export const zLogin = z.object({
  email: z.email().trim().toLowerCase(),
  password: z.string().min(1).max(200),
});

export const zPassword = z.string().min(10, 'mínimo de 10 caracteres').max(200);

export const zUserCreate = z.object({
  email: z.email().trim().toLowerCase(),
  name: required(120),
  role: z.enum(USER_ROLES).default('member'),
  password: zPassword,
});

export const zUserUpdate = z.object({
  name: required(120).optional(),
  role: z.enum(USER_ROLES).optional(),
  active: z.boolean().optional(),
  password: zPassword.optional(),
});

export const zChangePassword = z.object({ currentPassword: z.string().min(1), newPassword: zPassword });

export const zAgentCreate = z.object({
  name: required(100),
  description: text(500).optional(),
  scopes: z.array(z.enum(AGENT_SCOPES)).min(1).default(['projects:read', 'tasks:read', 'tasks:write', 'labels:write', 'executions:write']),
  projectKeys: z.array(zProjectKey).max(100).default([]).describe('Vazio = todos os projetos'),
});

export const zAgentUpdate = z.object({
  description: text(500).optional(),
  scopes: z.array(z.enum(AGENT_SCOPES)).min(1).optional(),
  projectKeys: z.array(zProjectKey).max(100).optional(),
  active: z.boolean().optional(),
});

export const zBoardColumn = z.object({
  status: zStatus,
  title: required(60),
  wipLimit: z.number().int().min(0).max(999).nullable().optional(),
});

export const zBoardUpdate = z.object({
  name: required(80).optional(),
  columns: z
    .array(zBoardColumn)
    .min(1)
    .max(TASK_STATUSES.length)
    .refine((cols) => new Set(cols.map((c) => c.status)).size === cols.length, 'cada status só pode aparecer em uma coluna')
    .optional(),
});

// ---------- Tempo ----------

export const zWorklog = z.object({
  minutes: z.number().int().min(1).max(24 * 60).describe('Duração em minutos'),
  startedAt: zDate.optional().describe('Quando o trabalho começou (padrão: agora − duração)'),
  note: text(1000).optional(),
});

export const zTimerStop = z.object({
  note: text(1000).optional(),
});

export const zTimeReportQuery = z.object({
  from: zDate.describe('Início (data ou data/hora ISO)'),
  to: zDate.describe('Fim (data ou data/hora ISO; data sem hora = até o fim do dia)'),
  project: csv(zProjectKey),
  actor: csv(z.string().regex(/^(user|agent):[\w-]+$/, 'use user:<id> ou agent:<id>')),
  tz: z.string().max(64).default('America/Sao_Paulo').describe('Fuso para agrupar por dia'),
  entries: bool.describe('Inclui a lista de apontamentos (para exportar)'),
});

// ---------- Releases ----------

const zReleaseLink = z.object({ label: required(60), url: zUrl });
const releaseFields = {
  component: z.string().trim().max(40).nullable().optional().describe('Chave do módulo do projeto (frente/app); null = projeto inteiro'),
  type: z.enum(RELEASE_TYPES).nullable().optional().describe('major, minor, patch ou hotfix'),
  buildNumber: text(30).optional().describe('Build/versionCode, ex.: 42'),
  platforms: z.array(z.enum(RELEASE_PLATFORMS)).max(RELEASE_PLATFORMS.length).optional(),
  description: text(5000).optional(),
  startDate: zDate.nullable().optional(),
  targetDate: zDate.nullable().optional(),
  gitTag: text(120).optional().describe('Tag Git, ex.: app-cliente/v1.4.0'),
  owner: z.union([zActorRef, z.literal('me')]).nullable().optional().describe('Responsável pela versão'),
  links: z.array(zReleaseLink).max(10).optional().describe('Loja, download, changelog, PR...'),
};

export const zReleaseCreate = z.object({
  projectKey: zProjectKey,
  name: required(60).describe('Versão, ex.: 1.4.0'),
  ...releaseFields,
});

export const zReleaseUpdate = z.object({
  name: required(60).optional(),
  ...releaseFields,
  notes: text(50_000).optional().describe('Notas de versão (Markdown)'),
  status: z.enum(['planned', 'in_progress', 'testing', 'canceled']).optional().describe('Avança/volta a etapa, cancela ou reabre (planned) uma versão não publicada'),
});

export const zReleasePublish = z.object({
  notes: text(50_000).optional().describe('Notas finais; vazio = gera automaticamente'),
  moveOpenTo: z.string().regex(/^[a-f0-9]{24}$/).optional().describe('Versão aberta que recebe as tarefas ainda abertas'),
  force: z.boolean().default(false).describe('Somente admin: publica mesmo com tarefas abertas (elas continuam vinculadas)'),
});

export const zReleaseQuery = z.object({
  project: zProjectKey.optional(),
  status: csv(z.enum(RELEASE_STATUSES)),
  component: z.string().trim().max(40).optional().describe('Chave do módulo; "none" = versões do projeto inteiro'),
});

// ---------- Modelos de tarefa ----------

export const zTemplateCreate = z.object({
  name: required(80),
  description: text(300).optional().describe('Quando usar este modelo'),
  projectKey: zProjectKey.nullable().optional().describe('null/ausente = modelo global (só administradores)'),
  type: zType.default('task'),
  priority: zPriority.optional(),
  titlePrefix: text(40).optional().describe('Prefixo sugerido no título, ex.: "[Bug] "'),
  body: text(20_000).optional().describe('Descrição (Markdown) que a tarefa recebe'),
  acceptanceCriteria: z.array(required(1000)).max(30).default([]),
  checklist: z.array(required(500)).max(50).default([]),
  labels: z.array(z.string().trim().min(1).max(50)).max(15).default([]),
  estimateMinutes: z.number().int().min(0).max(100_000).optional(),
});

export const zTemplateUpdate = zTemplateCreate.omit({ projectKey: true }).partial().extend({
  priority: zPriority.nullable().optional(),
  estimateMinutes: z.number().int().min(0).max(100_000).nullable().optional(),
});

// ---------- Visões salvas ----------

export const VIEW_PAGES = ['board', 'backlog', 'tasks', 'time_report', 'roadmap', 'releases', 'search'] as const;
export const zViewParams = z
  .record(z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,30}$/), z.string().max(500))
  .refine((r) => Object.keys(r).length <= 30, 'no máximo 30 filtros');

export const zViewCreate = z.object({
  name: required(60),
  page: z.enum(VIEW_PAGES),
  params: zViewParams,
  shared: z.boolean().default(false).describe('Visível para todos os usuários'),
});

export const zViewUpdate = z.object({ name: required(60).optional(), params: zViewParams.optional(), shared: z.boolean().optional() });

// ---------- Fila de agentes ----------

export const zAgentNext = z.object({
  project: zProjectKey.optional().describe('Padrão: qualquer projeto que o agente acessa'),
  dryRun: z.boolean().default(false).describe('Só mostra qual seria a próxima, sem pegar'),
});

export const zAgentReportQuery = z.object({
  from: zDate.describe('Início (data ou data/hora ISO)'),
  to: zDate.describe('Fim (data sem hora = até o fim do dia)'),
  project: csv(zProjectKey),
});

// ---------- Sprints ----------

const zCapacity = z.array(z.object({ actor: z.union([zActorRef, z.literal('me')]), minutes: z.number().int().min(0).max(100_000) })).max(50);

export const zSprintCreate = z.object({
  projectKey: zProjectKey,
  name: required(60),
  goal: text(2000).optional(),
  startDate: zDate,
  endDate: zDate,
  capacity: zCapacity.default([]).describe('Capacidade em minutos por pessoa/agente na sprint'),
});

export const zSprintUpdate = z.object({
  name: required(60).optional(),
  goal: text(2000).optional(),
  startDate: zDate.optional(),
  endDate: zDate.optional(),
  capacity: zCapacity.optional(),
});

export const zSprintClose = z.object({
  moveOpenTo: z.string().regex(/^[a-f0-9]{24}$/).optional().describe('Sprint planejada que recebe as tarefas abertas; vazio = voltam para o backlog'),
});

export const zSprintQuery = z.object({
  project: zProjectKey.optional(),
  status: csv(z.enum(['planned', 'active', 'closed'])),
});

// ---------- Notificações ----------
const zNotifType = z.enum(['assigned', 'mentioned', 'replied', 'blocked', 'overdue', 'digest']);
export const zNotificationPrefs = z.object({
  email: z.boolean().optional(),
  types: z.partialRecord(zNotifType, z.object({ app: z.boolean().optional(), email: z.boolean().optional() })).optional(),
});
export const zNotificationsRead = z.object({ ids: z.array(z.string().regex(/^[a-f0-9]{24}$/)).max(200).optional(), all: z.boolean().default(false) });

// ---------- Documentos do projeto ----------

export const DOCUMENT_CONTENT_MAX = 200_000;
const zObjectIdStr = z.string().regex(/^[a-f0-9]{24}$/, 'id inválido');

export const zDocumentCreate = z.object({
  title: required(200),
  content: z.string().max(DOCUMENT_CONTENT_MAX).default('').describe('Conteúdo em Markdown'),
  parentId: zObjectIdStr.nullable().optional().describe('Página-pai (mesmo projeto); ausente = raiz'),
});

export const zDocumentUpdate = z.object({
  title: required(200).optional(),
  content: z.string().max(DOCUMENT_CONTENT_MAX).optional(),
  parentId: zObjectIdStr.nullable().optional().describe('Move a página (null = raiz)'),
  position: z.number().int().min(0).max(1_000_000).optional().describe('Ordem entre as irmãs'),
  expectedVersion: z.number().int().optional().describe('Controle de concorrência otimista'),
});

export const zDocumentQuery = z.object({
  q: z.string().trim().max(200).optional().describe('Busca no título e no conteúdo'),
});
